import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, Users, Layout, Filter } from "lucide-react";
import { joinUnitsWithWeapons } from "@/lib/utils/unit-weapons-joined";
import { calculatePatchDiff } from "@/lib/utils/patch-diff";
import { calculateDivisionDiff } from "@/lib/utils/division-diff";
import { UnitChangeCard } from "@/components/patch-notes/UnitChangeCard";
import { DivisionChangeCard } from "@/components/patch-notes/DivisionChangeCard";
import { FieldFilterModal } from "@/components/patch-notes/FieldFilterModal";
import { collectAllFields } from "@/components/patch-notes/collect-fields";
import { shouldShowField } from "@/components/patch-notes/field-filter";
import { shouldOmitField } from "@/components/patch-notes/patch-notes-omit";
import { extractDiffValues } from "@/lib/utils/patch-diff";
import type { Unit } from "@/lib/types/unit";
import type { Division } from "@/lib/types/deck-builder";
import type { WeaponsData } from "@/lib/utils/unit-weapons-joined";
import type { UnitWithWeapons } from "@/lib/utils/unit-weapons-joined";
import type { DiffResult } from "@/lib/utils/patch-diff";

interface Version {
  version: string;
  file_size: number;
  checksum: string | null;
  created_at: string;
  updated_at: string;
  metadata: any;
}

type ViewMode = "unit" | "division";

export function PatchNotes() {
  const [unitsVersions, setUnitsVersions] = useState<Version[]>([]);
  const [weaponsVersions, setWeaponsVersions] = useState<Version[]>([]);
  const [divisionsVersions, setDivisionsVersions] = useState<Version[]>([]);
  const [oldVersion, setOldVersion] = useState<string>("");
  const [newVersion, setNewVersion] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewMode>("unit");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data for both versions
  const [oldUnits, setOldUnits] = useState<Unit[]>([]);
  const [oldWeapons, setOldWeapons] = useState<WeaponsData | null>(null);
  const [oldDivisions, setOldDivisions] = useState<Division[]>([]);
  const [newUnits, setNewUnits] = useState<Unit[]>([]);
  const [newWeapons, setNewWeapons] = useState<WeaponsData | null>(null);
  const [newDivisions, setNewDivisions] = useState<Division[]>([]);

  // Calculated diffs
  const [patchDiff, setPatchDiff] = useState<ReturnType<typeof calculatePatchDiff> | null>(null);
  const [divisionDiff, setDivisionDiff] = useState<ReturnType<typeof calculateDivisionDiff> | null>(null);
  
  // Field filtering
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [availableFields, setAvailableFields] = useState<Set<string>>(new Set());
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Fetch available versions
  useEffect(() => {
    async function fetchVersions() {
      try {
        const [unitsRes, weaponsRes, divisionsRes] = await Promise.all([
          fetch("/api/units/versions"),
          fetch("/api/weapons/versions"),
          fetch("/api/divisions/versions"),
        ]);

        if (!unitsRes.ok || !weaponsRes.ok || !divisionsRes.ok) {
          throw new Error("Failed to fetch versions");
        }

        const units = await unitsRes.json();
        const weapons = await weaponsRes.json();
        const divisions = await divisionsRes.json();

        setUnitsVersions(units);
        setWeaponsVersions(weapons);
        setDivisionsVersions(divisions);

        // Set default versions (latest and previous)
        if (units.length >= 2) {
          setNewVersion(units[0].version);
          setOldVersion(units[1].version);
        } else if (units.length === 1) {
          setNewVersion(units[0].version);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    }

    fetchVersions();
  }, []);

  // Fetch data for selected versions and calculate diffs
  useEffect(() => {
    if (!oldVersion || !newVersion) {
      return;
    }

    async function fetchDataAndCalculateDiff() {
      setLoading(true);
      setError(null);

      try {
        // Fetch all data for both versions in parallel
        const [
          oldUnitsRes,
          oldWeaponsRes,
          oldDivisionsRes,
          newUnitsRes,
          newWeaponsRes,
          newDivisionsRes,
        ] = await Promise.all([
          fetch(`/api/units/${oldVersion}`),
          fetch(`/api/weapons/${oldVersion}`),
          fetch(`/api/divisions/${oldVersion}`),
          fetch(`/api/units/${newVersion}`),
          fetch(`/api/weapons/${newVersion}`),
          fetch(`/api/divisions/${newVersion}`),
        ]);

        if (
          !oldUnitsRes.ok ||
          !oldWeaponsRes.ok ||
          !oldDivisionsRes.ok ||
          !newUnitsRes.ok ||
          !newWeaponsRes.ok ||
          !newDivisionsRes.ok
        ) {
          throw new Error("Failed to fetch version data");
        }

        const oldUnitsData = await oldUnitsRes.json();
        const oldWeaponsData = await oldWeaponsRes.json();
        const oldDivisionsData = await oldDivisionsRes.json();
        const newUnitsData = await newUnitsRes.json();
        const newWeaponsData = await newWeaponsRes.json();
        const newDivisionsData = await newDivisionsRes.json();

        // Parse units arrays
        const oldUnitsArray = Array.isArray(oldUnitsData) ? oldUnitsData : oldUnitsData.units || [];
        const newUnitsArray = Array.isArray(newUnitsData) ? newUnitsData : newUnitsData.units || [];

        // Parse weapons (weapons data has structure: { weapons: {...} } or is already the weapons object)
        const oldWeaponsObj = oldWeaponsData.weapons || oldWeaponsData;
        const newWeaponsObj = newWeaponsData.weapons || newWeaponsData;

        // Parse divisions arrays
        const oldDivisionsArray = Array.isArray(oldDivisionsData)
          ? oldDivisionsData
          : oldDivisionsData.divisions || [];
        const newDivisionsArray = Array.isArray(newDivisionsData)
          ? newDivisionsData
          : newDivisionsData.divisions || [];

        setOldUnits(oldUnitsArray);
        setOldWeapons(oldWeaponsObj);
        setOldDivisions(oldDivisionsArray);
        setNewUnits(newUnitsArray);
        setNewWeapons(newWeaponsObj);
        setNewDivisions(newDivisionsArray);

        // Join units with weapons
        const oldUnitsWithWeapons = joinUnitsWithWeapons(oldUnitsArray, oldWeaponsObj);
        const newUnitsWithWeapons = joinUnitsWithWeapons(newUnitsArray, newWeaponsObj);

        // Calculate patch diff
        const patchDiffResult = calculatePatchDiff(oldUnitsWithWeapons, newUnitsWithWeapons);
        setPatchDiff(patchDiffResult);

        // Collect all available fields from the diff
        const fields = collectAllFields(patchDiffResult);
        
        // Calculate division diff
        const divisionDiffResult = calculateDivisionDiff(
          oldDivisionsArray,
          newDivisionsArray,
          patchDiffResult
        );
        setDivisionDiff(divisionDiffResult);
        
        // Add "availability" field if any division has availability changes
        const hasAvailabilityChanges = Object.values(divisionDiffResult).some(
          (divisionChange) => divisionChange.availabilityChanges.length > 0
        );
        if (hasAvailabilityChanges) {
          fields.add("availability");
        }
        
        setAvailableFields(fields);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    }

    fetchDataAndCalculateDiff();
  }, [oldVersion, newVersion]);

  const availableVersions = unitsVersions.map((v) => v.version);

  /**
   * Check if a diff has any visible changes after filtering
   * This is a simplified version of the logic in UnitChangeCard
   */
  function hasVisibleChanges(diff: DiffResult, selectedFields: Set<string> | undefined): boolean {
    if (!diff || typeof diff !== "object") {
      return false;
    }

    // If no fields are selected, all changes are visible
    if (!selectedFields || selectedFields.size === 0) {
      return Object.keys(diff).length > 0;
    }

    // Recursively check for visible changes
    // At this point, selectedFields is guaranteed to be defined and non-empty
    const fields = selectedFields;
    function checkDiff(obj: any, path: string = ""): boolean {
      if (!obj || typeof obj !== "object") {
        return false;
      }

      // Check if it's a direct diff array [oldValue, newValue]
      const directDiff = extractDiffValues(obj);
      if (directDiff) {
        const fullPath = path || "root";
        return !shouldOmitField(fullPath) && shouldShowField(fullPath, fields);
      }

      // Recursively check object properties
      for (const [key, value] of Object.entries(obj)) {
        // Skip internal fields
        if (key.startsWith("_")) {
          continue;
        }

        const newPath = path ? `${path}.${key}` : key;
        
        // Skip omitted fields
        if (shouldOmitField(newPath)) {
          continue;
        }
        
        // Handle array diff format (for divisions, specialities, etc.)
        // Check if this is an array of diff markers like [['+', value], ['-', value]]
        // jsondiffpatch stores array diffs as arrays where each element is a diff marker
        if (Array.isArray(value) && value.length > 0) {
          // Check if first element is a diff marker array (format: ['+', value], ['-', value], etc.)
          const firstElement = value[0];
          if (Array.isArray(firstElement) && firstElement.length >= 2 && typeof firstElement[0] === "string") {
            // This is an array of diff markers - if the field is selected, we have changes
            // For specialities and divisions, check if the field name itself is selected
            const isSpecialitiesOrDivisions = key === "specialities" || key === "divisions" || newPath === "specialities" || newPath === "divisions";
            if (isSpecialitiesOrDivisions && fields.has(key)) {
              // Direct match on field name
              return true;
            }
            // Direct check: if the field name matches, or if shouldShowField says it matches
            if (fields.has(newPath) || shouldShowField(newPath, fields)) {
              return true;
            }
            continue;
          }
        }
        
        // Also handle the case where specialities/divisions is stored as an object with numeric keys
        // e.g., { specialities: { "0": ['+', value], "1": ['-', value] } }
        const keyIsNumericForArray = /^\d+$/.test(key);
        const isSimpleStringArrayField = path === "specialities" || path === "divisions";
        // Check if this is a diff marker (format: ['+', value] or ['-', value])
        const isDiffMarker = Array.isArray(value) && value.length >= 2 && typeof value[0] === "string" && (value[0] === '+' || value[0] === '-' || value[0] === '~');
        if (keyIsNumericForArray && isSimpleStringArrayField && isDiffMarker) {
          // This is a diff marker array under a simple string array field
          // Direct check: if the parent field name matches, or if shouldShowField says it matches
          if (fields.has(path) || shouldShowField(path, fields)) {
            return true;
          }
          continue;
        }
        
        // Also check if we're at the "specialities" or "divisions" key and the value is an object
        // jsondiffpatch may store array diffs as objects with _t: 'a' marker and numeric keys
        // Format: { specialities: { _t: 'a', 0: ['+', value], 1: ['-', value] } }
        if ((key === "specialities" || key === "divisions") && value && typeof value === "object" && !Array.isArray(value)) {
          // Check if this is a jsondiffpatch array diff (has _t: 'a' marker)
          const isArrayDiff = (value as any)._t === 'a';
          // Or check if it contains numeric keys with diff markers
          const hasDiffMarkers = Object.keys(value).some(k => {
            if (k === '_t') return false; // Skip the _t marker
            const v = (value as any)[k];
            return Array.isArray(v) && v.length >= 2 && typeof v[0] === "string" && (v[0] === '+' || v[0] === '-' || v[0] === '~');
          });
          
          if (isArrayDiff || hasDiffMarkers) {
            // Direct check: if the field name matches, or if shouldShowField says it matches
            if (fields.has(key) || fields.has(newPath) || shouldShowField(newPath, fields)) {
              return true;
            }
            continue;
          }
        }
        
        // Check if any selected field pattern could match paths starting with the current path
        // This is needed for nested paths like "weapons.*.ammo.aimingTime"
        // We need to check this for ALL keys, not just numeric ones
        let shouldRecurseForNested = false;
        if (value && typeof value === "object" && !Array.isArray(value)) {
          const normalizedPath = newPath.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
          
          for (const selectedField of fields) {
            const normalizedSelected = selectedField.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
            // Check if selected field starts with current path (reverse prefix match)
            // e.g., "weapons.*.ammo.aimingTime" starts with "weapons"
            if (normalizedSelected.startsWith(normalizedPath + ".") || 
                normalizedSelected.startsWith(normalizedPath + "[")) {
              shouldRecurseForNested = true;
              break;
            }
            // Also check segment-by-segment if selected field could match nested paths
            const pathSegments = normalizedPath.split(".");
            const selectedSegments = normalizedSelected.split(".");
            if (pathSegments.length <= selectedSegments.length) {
              let matches = true;
              for (let i = 0; i < pathSegments.length; i++) {
                const pathSeg = pathSegments[i];
                const selectedSeg = selectedSegments[i];
                if (!pathSeg || !selectedSeg) {
                  matches = false;
                  break;
                }
                if (selectedSeg === "*" || selectedSeg === ".*") {
                  continue;
                }
                const pathSegment = pathSeg.replace(/\.\*/g, "");
                const selectedSegment = selectedSeg.replace(/\.\*/g, "");
                if (pathSegment !== selectedSegment) {
                  matches = false;
                  break;
                }
              }
              if (matches) {
                shouldRecurseForNested = true;
                break;
              }
            }
          }
        }
        
        // Skip fields that aren't in selectedFields
        // But first check if this is a numeric key (array index) - if so, check the parent path too
        const isNumericKey = /^\d+$/.test(key);
        
        // For numeric keys, we need to check if the parent path or any nested path would match
        // For example, if we have weapons.0.ammo.heDamage and weapons.*.ammo.heDamage is selected
        if (isNumericKey && path) {
          // Check if any selected field could match paths starting with the current path
          // e.g., if selected is "weapons.*.ammo.heDamage" and we're at "weapons", we should recurse
          let shouldRecurse = false;
          const normalizedPath = path.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
          
          for (const selectedField of fields) {
            const normalizedSelected = selectedField.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
            // Check if selected field starts with current path (reverse prefix match)
            // e.g., "weapons.*.ammo.heDamage" starts with "weapons"
            if (normalizedSelected.startsWith(normalizedPath + ".") || 
                normalizedSelected.startsWith(normalizedPath + "[")) {
              shouldRecurse = true;
              break;
            }
            // Also check segment-by-segment if selected field could match nested paths
            const pathSegments = normalizedPath.split(".");
            const selectedSegments = normalizedSelected.split(".");
            if (pathSegments.length <= selectedSegments.length) {
              let matches = true;
              for (let i = 0; i < pathSegments.length; i++) {
                const pathSeg = pathSegments[i];
                const selectedSeg = selectedSegments[i];
                if (!pathSeg || !selectedSeg) {
                  matches = false;
                  break;
                }
                if (selectedSeg === "*" || selectedSeg === ".*") {
                  continue;
                }
                const pathSegment = pathSeg.replace(/\.\*/g, "");
                const selectedSegment = selectedSeg.replace(/\.\*/g, "");
                if (pathSegment !== selectedSegment) {
                  matches = false;
                  break;
                }
              }
              if (matches) {
                shouldRecurse = true;
                break;
              }
            }
          }
          
          if (shouldRecurse && value && typeof value === "object" && !Array.isArray(value)) {
            // Parent path could match, so recurse to find nested matches
            if (checkDiff(value, newPath)) {
              return true;
            }
          }
          
          // Also check if the full path with this numeric key would match
          if (shouldShowField(newPath, fields)) {
            // Continue to recursive check below
          } else if (!shouldRecurse) {
            continue;
          }
        }
        
        const shouldShow = shouldShowField(newPath, fields) || 
                          (isNumericKey && path && shouldShowField(path, fields)) ||
                          shouldRecurseForNested;
        
        if (!shouldShow && !isNumericKey && !shouldRecurseForNested) {
          continue;
        }

        // Check if this field has a visible change
        const diffValues = extractDiffValues(value);
        if (diffValues) {
          return true;
        }

        // Recursively check nested objects
        if (value && typeof value === "object" && !Array.isArray(value)) {
          if (checkDiff(value, newPath)) {
            return true;
          }
        } else if (Array.isArray(value) && value.length > 0 && typeof value[0] !== "string") {
          // Check array elements
          for (let i = 0; i < value.length; i++) {
            const arrayItemPath = `${newPath}[${i}]`;
            if (!shouldOmitField(arrayItemPath) && shouldShowField(arrayItemPath, fields)) {
              if (checkDiff(value[i], arrayItemPath)) {
                return true;
              }
            }
          }
        }
      }

      return false;
    }

    return checkDiff(diff);
  }

  // Filter changed units to only include those with visible changes
  const visibleChangedUnits = patchDiff?.changed.filter((change) => {
    if (!selectedFields || selectedFields.size === 0) {
      return true; // Show all when no filter is active
    }
    return hasVisibleChanges(change.diff, selectedFields);
  }) || [];

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Patch Notes</h1>
          <p className="text-muted-foreground">
            Compare changes between different versions of units, weapons, and divisions.
          </p>
        </div>

      {/* Version Selectors */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Versions to Compare</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="flex-1 w-full sm:w-auto">
              <label className="text-sm font-medium mb-2 block">Old Version</label>
              <Select value={oldVersion} onValueChange={setOldVersion}>
                <SelectTrigger>
                  <SelectValue placeholder="Select old version" />
                </SelectTrigger>
                <SelectContent>
                  {availableVersions.map((version) => (
                    <SelectItem key={version} value={version}>
                      {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 w-full sm:w-auto">
              <label className="text-sm font-medium mb-2 block">New Version</label>
              <Select value={newVersion} onValueChange={setNewVersion}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new version" />
                </SelectTrigger>
                <SelectContent>
                  {availableVersions.map((version) => (
                    <SelectItem key={version} value={version}>
                      {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto min-w-0">
              <Button
                variant={viewMode === "unit" ? "default" : "outline"}
                onClick={() => setViewMode("unit")}
                className="flex-1 sm:flex-initial min-w-0"
              >
                <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">Unit View</span>
              </Button>
              <Button
                variant={viewMode === "division" ? "default" : "outline"}
                onClick={() => setViewMode("division")}
                className="flex-1 sm:flex-initial min-w-0"
              >
                <Layout className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">Division View</span>
              </Button>
              {patchDiff && patchDiff.changed.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setFilterModalOpen(true)}
                  className="flex-1 sm:flex-initial min-w-0"
                >
                  <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Filter Fields</span>
                  {selectedFields.size > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded flex-shrink-0">
                      {selectedFields.size}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading and calculating diffs...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-red-500">
          <CardContent className="pt-6">
            <div className="text-red-600 dark:text-red-400">Error: {error}</div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!loading && !error && oldVersion && newVersion && (
        <div>
          {viewMode === "unit" && patchDiff && (
            <div className="space-y-6">
              {/* Added Units */}
              {patchDiff.added.length > 0 && (
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="added-units" className="border rounded-lg px-4">
                    <AccordionTrigger className="text-2xl font-bold hover:no-underline">
                      New Units ({patchDiff.added.length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                        {patchDiff.added.map((unit) => {
                          // Combine old and new divisions to ensure we have all division data
                          const allDivisions = [...newDivisions, ...oldDivisions];
                          const divisionsMap = new Map(allDivisions.map(d => [d.descriptor, d]));
                          
                          return (
                            <UnitChangeCard
                              key={unit.descriptorName}
                              unit={unit}
                              diff={{}}
                              changeType="added"
                              divisionsMap={divisionsMap}
                              selectedFields={selectedFields}
                            />
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}

              {/* Removed Units */}
              {patchDiff.removed.length > 0 && (
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="removed-units" className="border rounded-lg px-4">
                    <AccordionTrigger className="text-2xl font-bold hover:no-underline">
                      Removed Units ({patchDiff.removed.length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                        {patchDiff.removed.map((unit) => {
                          // Combine old and new divisions to ensure we have all division data
                          const allDivisions = [...newDivisions, ...oldDivisions];
                          const divisionsMap = new Map(allDivisions.map(d => [d.descriptor, d]));
                          
                          return (
                            <UnitChangeCard
                              key={unit.descriptorName}
                              unit={unit}
                              diff={{}}
                              changeType="removed"
                              divisionsMap={divisionsMap}
                              selectedFields={selectedFields}
                            />
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}

              {/* Changed Units */}
              {visibleChangedUnits.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold mb-4">
                    Changed Units ({visibleChangedUnits.length}
                    {selectedFields && selectedFields.size > 0 && patchDiff.changed.length !== visibleChangedUnits.length
                      ? ` of ${patchDiff.changed.length}`
                      : ""})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {visibleChangedUnits.map((change) => {
                      // Combine old and new divisions to ensure we have all division data
                      const allDivisions = [...newDivisions];
                      oldDivisions.forEach((oldDiv) => {
                        if (!allDivisions.find(d => d.descriptor === oldDiv.descriptor)) {
                          allDivisions.push(oldDiv);
                        }
                      });
                      const divisionsMap = new Map(allDivisions.map(d => [d.descriptor, d]));
                      
                      return (
                        <UnitChangeCard
                          key={change.unit.descriptorName}
                          unit={change.unit}
                          oldUnit={change.oldUnit}
                          diff={change.diff}
                          changeType="changed"
                          divisionsMap={divisionsMap}
                          selectedFields={selectedFields}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {patchDiff.added.length === 0 &&
                patchDiff.removed.length === 0 &&
                patchDiff.changed.length === 0 && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center text-muted-foreground">
                        No changes found between these versions.
                      </div>
                    </CardContent>
                  </Card>
                )}
            </div>
          )}

          {viewMode === "division" && divisionDiff && (
            <div className="space-y-6">
              {Object.keys(divisionDiff).length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center text-muted-foreground">
                      No division changes found between these versions.
                    </div>
                  </CardContent>
                </Card>
              ) : (
                Object.entries(divisionDiff).map(([divisionDescriptor, change]) => {
                  // Combine old and new divisions to ensure we have all division data
                  const allDivisions = [...newDivisions, ...oldDivisions];
                  const divisionsMap = new Map(allDivisions.map(d => [d.descriptor, d]));
                  
                  return (
                    <DivisionChangeCard
                      key={divisionDescriptor}
                      divisionChange={change}
                      unitMap={newUnits.reduce((acc, unit) => {
                        acc[unit.descriptorName] = unit;
                        return acc;
                      }, {} as Record<string, Unit>)}
                      divisionsMap={divisionsMap}
                      selectedFields={selectedFields}
                    />
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {!loading && !error && (!oldVersion || !newVersion) && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              Please select both old and new versions to compare.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Field Filter Modal */}
      <FieldFilterModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        availableFields={availableFields}
        selectedFields={selectedFields}
        onApply={setSelectedFields}
      />
      </div>
    </div>
  );
}

